'use client';

import Image from 'next/image';

/** Beautiful loading element: simulates handwriting the Dank wordmark.
 *
 *  Layered:
 *   1. Faded ghost of the logo (always visible), gives the "outline" anchor.
 *   2. Full-color logo on top, revealed left-to-right via animated `clip-path`,
 *      then briefly held and faded out so the loop restarts cleanly.
 *   3. A diagonal shimmer sweep with `mix-blend-mode: overlay` for life.
 *   4. Three pulsing dots underneath as a secondary "still loading" cue.
 *
 *  Accessibility: `role="status"` + visually-hidden label so screen readers
 *  announce loading. `prefers-reduced-motion` disables every animation. */

type Size = 'sm' | 'md' | 'lg';

const SIZE_CLASS: Record<Size, string> = {
  sm: 'w-24',
  md: 'w-40',
  lg: 'w-56',
};

export default function LogoLoader({
  size = 'md',
  label = 'Loading…',
  className = '',
}: {
  size?: Size;
  label?: string;
  className?: string;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={`logo-loader inline-flex flex-col items-center gap-3 ${className}`}
    >
      <div className={`logo-loader__stage relative ${SIZE_CLASS[size]}`}>
        {/* Ghost outline */}
        <Image
          src="/media/brand/logo.webp"
          width={256}
          height={132}
          alt=""
          aria-hidden
          draggable={false}
          className="logo-loader__ghost block w-full h-auto select-none"
          sizes="(max-width: 768px) 160px, 224px"
        />
        {/* Drawing reveal */}
        <Image
          src="/media/brand/logo.webp"
          width={256}
          height={132}
          alt=""
          aria-hidden
          draggable={false}
          className="logo-loader__draw absolute inset-0 block w-full h-auto select-none"
          sizes="(max-width: 768px) 160px, 224px"
        />
        {/* Shimmer sweep */}
        <span aria-hidden className="logo-loader__shimmer pointer-events-none absolute inset-0 block overflow-hidden" />
      </div>
      <div aria-hidden className="logo-loader__dots flex items-center gap-1.5">
        <span className="logo-loader__dot h-1.5 w-1.5 rounded-full bg-blue-100" />
        <span className="logo-loader__dot h-1.5 w-1.5 rounded-full bg-blue-100" />
        <span className="logo-loader__dot h-1.5 w-1.5 rounded-full bg-blue-100" />
      </div>
      <span className="sr-only">{label}</span>
    </div>
  );
}
